import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BillingErrorsService } from '@brand-lolly/app/billing/services/billing-errors.service';
import { DowngradeSubscriptionService } from '@brand-lolly/app/billing/services/downgrade-subscription.service';
import { CatalogService } from '@brand-lolly/app/billing/services/catalog.service';
import { GetSubscriptionsService } from '@brand-lolly/app/billing/services/get-subscriptions.service';
import { ReleaseScheduledChangeService } from '@brand-lolly/app/billing/services/release-scheduled-change.service';
import { StartCheckoutService } from '@brand-lolly/app/billing/services/start-checkout.service';
import { SubscriptionsStateService } from '@brand-lolly/app/billing/services/subscriptions-state.service';
import { CreditBalanceStateService } from '@brand-lolly/app/billing/services/credit-balance-state.service';
import { UncancelSubscriptionService } from '@brand-lolly/app/billing/services/uncancel-subscription.service';
import { UpgradeSubscriptionService } from '@brand-lolly/app/billing/services/upgrade-subscription.service';
import { humanizeAlias } from '@brand-lolly/app/billing/types/alias-labels';
import { CatalogPlan } from '@brand-lolly/app/billing/types/plan.types';
import { Product } from '@brand-lolly/app/billing/types/product.types';
import {
	buildProductTabs,
	defaultProductTab,
	ProductTabOption
} from '@brand-lolly/app/billing/product-tabs/product-tabs.util';
import { ProductSubscription } from '@brand-lolly/app/billing/types/subscription.types';
import {
	PlanEntitlementAlias,
	PlanInterval,
	ProductAlias,
	ScheduledChangeType,
	SubscriptionPhase,
	TrialKind
} from '@brand-lolly/app/billing/types/billing.enums';
import {
	ApiTierView,
	CreatorDataApiCardComponent
} from '@brand-lolly/app/billing/creator-data-api-card/creator-data-api-card.component';
import {
	PlanCardComponent,
	PlanCardRow
} from '@brand-lolly/app/billing/plan-card/plan-card.component';
import {
	buildPlanLadder,
	PlanCardLadderStop
} from '@brand-lolly/app/billing/plan-card/plan-ladder.model';
import { buildFeatureRows } from '@brand-lolly/app/billing/plan-card/plan-card-rows.util';
import { PlanDetailsDialogService } from '@brand-lolly/app/billing/dialogs/plan-details-dialog/plan-details-dialog.service';
import {
	ALLOWANCE_DIAL_LABEL,
	annualBillingNote,
	applicationTierCopy,
	ENTERPRISE_ALLOWANCE_LABEL,
	ENTERPRISE_TIER_COPY,
	planCardTitle
} from '@brand-lolly/app/billing/plan-card/plan-tier-copy';
import { PlanCardSkeletonComponent } from '@brand-lolly/app/billing/plan-card/plan-card-skeleton.component';
import { EnterpriseCardComponent } from '@brand-lolly/app/billing/enterprise-card/enterprise-card.component';
import { EnterpriseCardSkeletonComponent } from '@brand-lolly/app/billing/enterprise-card/enterprise-card-skeleton.component';
import { EnterprisePlan } from '@brand-lolly/app/billing/types/enterprise-plan.types';
import { PlanIntervalToggleComponent } from '@brand-lolly/app/billing/plan-interval/plan-interval-toggle.component';
import {
	computeAnnualFreeMonths,
	computeAnnualSavings,
	hasYearlyVariant,
	monthlyTwinPrice,
	planHasTrial,
	struckMonthlyPrice,
	toMonthlyPrice
} from '@brand-lolly/app/billing/plan-interval/plan-interval.util';
import {
	LoButton,
	LoEmptyState,
	LoNotice,
	LoSegmentedTabs,
	LoTab,
	LoToastService
} from 'lo-design-system';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { AnaChannelConnectService } from '@brand-lolly/app/_services/ana-channel-connect.service';

type PlanAction =
	| 'current'
	| 'subscribe'
	| 'upgrade'
	| 'downgrade'
	| 'scheduled'
	| 'unavailable';

interface PlanRow extends CatalogPlan {
	action: PlanAction;
	actionLabel: string;
	actionInFlight: boolean;
	/**
	 * True when the action moves the brand to another credit allowance of the
	 * tier and interval it already holds. Still an `upgrade` or a `downgrade`
	 * underneath, on the same endpoints; only the words differ.
	 */
	allowanceChange: boolean;
	/** The rung's monthly credits, for the allowance-change label and toast. */
	allowanceCredits: number | null;
	/** What the action will do, said before it is taken. Empty for none. */
	actionNote: string;
	monthlyPrice: string;
	/** Original monthly price shown struck through next to the annual price. */
	struckMonthlyPrice: string | null;
	hasTrial: boolean;
	featureRows: PlanCardRow[];
	/** The card's heading — the tier alone, so it holds still while the dial
	 *  moves. `title` stays the catalogue's own per-rung name, which is what the
	 *  upgrade/downgrade toasts name. */
	cardTitle: string;
	/** The tier's allowance rungs, empty on a product with no ladder. */
	ladder: PlanCardLadderStop[];
	/** Which rung this row IS, within {@link ladder}. */
	ladderIndex: number;
	/** The upsell under the rungs; empty where the tier has none. */
	ladderFooter: string;
	/** The one line under the tier's name (see `plan-tier-copy`). */
	subtitle: string;
	/** The note under the price — the free tier's terms, or the annual total. */
	priceNote: string;
}

/**
 * ONE CARD PER TIER, and this constant is what makes that true.
 *
 * The application product seeds one plan per (tier, rung, interval) — Pro has
 * five allowance rungs and Business nine — so the catalogue holds 29 shown rows
 * for what the pricing page calls three plans. The page grouped by PRODUCT and
 * rendered a card per row, which put fifteen cards side by side on the monthly
 * toggle and badged all nine Business rungs "Most Popular".
 *
 * So for this product the rows are grouped by `tier_level` and the rung becomes
 * a slider inside the card. Keyed off the plan alias rather than a product id,
 * which is assigned per environment.
 */
const PLATFORM_PLAN_PREFIX = `${ProductAlias.APPLICATION}_`;

@Component({
	standalone: true,
	selector: 'app-manage-subscription',
	templateUrl: './manage-subscription-plan.component.html',
	styleUrls: ['./manage-subscription-plan.component.scss'],
	imports: [
		LoButton,
		LoEmptyState,
		LoNotice,
		LoSegmentedTabs,
		PlanCardComponent,
		PlanCardSkeletonComponent,
		PlanIntervalToggleComponent,
		EnterpriseCardComponent,
		EnterpriseCardSkeletonComponent,
		CreatorDataApiCardComponent
	]
})
export class ManageSubscriptionPlanComponent implements OnInit, OnDestroy {
	loading = false;
	productTabs: ProductTabOption[] = [];
	productTabControl = new FormControl<ProductTabOption | null>(null);
	/** The product tabs as the strip draws them, built with {@link productTabs}. */
	productTabItems: LoTab[] = [];
	/** The id (the product id as text) of the tab the strip shows as chosen. */
	activeTabId = '';

	plansByProduct: Record<number, CatalogPlan[]> = {};
	subsByProduct: Partial<Record<number, ProductSubscription>> = {};
	activePlanRows: PlanRow[] = [];

	/** Creator Data API: slider cards (Report + Quick) instead of one-per-tier. */
	isCreatorDataApi = false;
	reportTiers: ApiTierView[] = [];
	quickTiers: ApiTierView[] = [];
	private apiRowsById: Record<number, PlanRow> = {};
	readonly reportFeatures = [
		'1 credit per full report',
		'Audience, growth & sponsor data',
		'Credits roll over during your term',
		'Instagram — more platforms soon'
	];
	readonly quickFeatures = [
		'0.05 credit per live lookup',
		'Fast lookups for vetting at scale',
		'Shared credit balance with Report',
		'Instagram — more platforms soon'
	];
	scheduledBanner: {
		changeType: ScheduledChangeType;
		targetPlanTitle: string | null;
		date: string;
		/** The sentence the banner says, built once with the banner. */
		message: string;
		productId: number;
	} | null = null;

	// Drives the loading skeleton grid — three plan placeholders.
	readonly skeletonCards = [0, 1, 2];

	// Copy shared with the public pricing page — see `plan-tier-copy`.
	readonly allowanceDialLabel = ALLOWANCE_DIAL_LABEL;
	readonly enterpriseCopy = ENTERPRISE_TIER_COPY;
	readonly enterpriseAllowanceLabel = ENTERPRISE_ALLOWANCE_LABEL;

	// Exposed for template comparisons (kills magic strings in the HTML).
	readonly ScheduledChangeType = ScheduledChangeType;
	scheduledActionInFlight = false;

	/** When true, show yearly plans; otherwise monthly. App-trial always shows. */
	annually = false;

	/** Whether the active product has yearly plan variants at all. */
	showAnnualToggle = false;
	/** Active product's max annual saving % (badge on the toggle); 0 hides it. */
	annualSavings = 0;
	/** Whole months the active product's year gives away; null falls back to the
	 *  percentage (see `computeAnnualFreeMonths`). */
	annualFreeMonths: number | null = null;

	/** Enterprise tiers keyed by product id (from the separate endpoint). */
	enterpriseByProduct: Record<number, EnterprisePlan> = {};
	/** Enterprise tier for the active product, or null when none. */
	activeEnterprisePlan: EnterprisePlan | null = null;
	/** Enterprise loads on its own endpoint — own skeleton while in flight. */
	enterpriseLoading = true;
	/** Tier of the active product's current plan (null when none). Hides the
	 *  "Most Popular" badge once the user already owns a higher tier. */
	currentPlanTierLevel: number | null = null;

	private activeProductId?: number;
	/** Rung the brand has chosen on each platform tier's slider, by tier level. */
	private platformRungByTier: Record<number, number> = {};
	private destroy$ = new Subject<void>();

	constructor(
		private catalogService: CatalogService,
		private subscriptionsService: GetSubscriptionsService,
		private checkoutService: StartCheckoutService,
		private upgradeService: UpgradeSubscriptionService,
		private downgradeService: DowngradeSubscriptionService,
		private uncancelService: UncancelSubscriptionService,
		private releaseService: ReleaseScheduledChangeService,
		private billingErrors: BillingErrorsService,
		private route: ActivatedRoute,
		private toasts: LoToastService,
		private planDetailsDialog: PlanDetailsDialogService,
		private subscriptionsState: SubscriptionsStateService,
		private creditBalanceState: CreditBalanceStateService,
		private anaConnect: AnaChannelConnectService
	) {}

	ngOnInit(): void {
		this.loadAll();
		this.loadEnterprisePlans();
		this.productTabControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(tab => this.recomputeActiveRows(tab?.productId));
	}

	private loadEnterprisePlans(): void {
		this.enterpriseLoading = true;
		this.catalogService
			.getEnterprisePlans()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: plans => {
					this.enterpriseByProduct = {};
					for (const plan of plans) {
						this.enterpriseByProduct[Number(plan.product_id)] =
							plan;
					}
					this.enterpriseLoading = false;
					this.recomputeActiveRows(this.activeProductId);
				},
				error: () => {
					this.enterpriseByProduct = {};
					this.enterpriseLoading = false;
				}
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Keyed by TIER on a laddered card and by plan id everywhere else.
	 *
	 * Moving the slider changes which plan the card is showing, so a key of
	 * `plan.id` would destroy and rebuild the card on every step of a drag —
	 * taking the slider's own focus with it. The tier is what the card is.
	 */
	trackByPlanCard(_: number, plan: PlanRow): string {
		return plan.ladder.length > 0
			? `tier:${plan.tier_level}`
			: `plan:${plan.id}`;
	}

	humanize(alias: string): string {
		return humanizeAlias(alias);
	}

	isPopular(plan: PlanRow): boolean {
		// Only the monthly Pro tier is flagged "Most Popular" — never the
		// yearly variant, so at most one card per product carries the badge.
		return plan.tier_level === 2 && plan.interval === PlanInterval.MONTH;
	}

	onPlanAction(plan: PlanRow): void {
		if (plan.actionInFlight || plan.action === 'current') {
			return;
		}
		if (plan.action === 'subscribe') {
			this.startCheckout(plan);
			return;
		}
		if (plan.action === 'upgrade') {
			this.upgradePlan(plan);
			return;
		}
		if (plan.action === 'downgrade') {
			this.downgradePlan(plan);
			return;
		}
	}

	private startCheckout(plan: PlanRow): void {
		plan.actionInFlight = true;
		const thankYouUrl = window.location.origin + '/thank-you';

		this.checkoutService
			.start({
				plan_id: plan.id,
				success_url: thankYouUrl,
				cancel_url: window.location.href
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: response => {
					plan.actionInFlight = false;
					if (response.kind === 'redirect' && response.url) {
						this.anaConnect.redirectExternally(
							response.url,
							'billing'
						);
					} else if (response.kind === 'app_trial_started') {
						this.toasts.success(
							'Free trial activated. Redirecting...'
						);
						setTimeout(() => {
							window.location.href = thankYouUrl;
						}, 700);
					}
				},
				error: err => {
					plan.actionInFlight = false;
					this.billingErrors.handle(err, 'Could not start checkout.');
				}
			});
	}

	private upgradePlan(plan: PlanRow): void {
		plan.actionInFlight = true;
		this.upgradeService
			.upgrade({ target_plan_id: plan.id })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					plan.actionInFlight = false;
					this.toasts.success(
						plan.allowanceChange
							? `Changed to ${this.creditsPhrase(
									plan.allowanceCredits
							  )} a month. The extra credits land today.`
							: `Upgraded to ${plan.title}.`
					);
					this.refreshSubscriptions();
				},
				error: err => {
					plan.actionInFlight = false;
					this.billingErrors.handle(err, 'Upgrade failed.');
				}
			});
	}

	private downgradePlan(plan: PlanRow): void {
		plan.actionInFlight = true;
		this.downgradeService
			.downgrade({ target_plan_id: plan.id })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: response => {
					plan.actionInFlight = false;
					const when = this.formatDate(response.scheduled_for);
					this.toasts.success(
						plan.allowanceChange
							? `Change to ${this.creditsPhrase(
									plan.allowanceCredits
							  )} a month scheduled for ${when}.`
							: `Downgrade to ${plan.title} scheduled for ${when}.`
					);
					this.refreshSubscriptions();
				},
				error: err => {
					plan.actionInFlight = false;
					this.billingErrors.handle(err, 'Downgrade failed.');
				}
			});
	}

	/**
	 * Re-read the subscriptions after a plan change — this page's own copy AND
	 * the app-wide store every other screen reads its tier and caps from.
	 *
	 * Only the first used to be re-read, so the seat cap, the tier and the
	 * feature gates everywhere else stayed on the old plan until a full page
	 * reload: an account that had just upgraded still could not invite on the
	 * Team page. The balance is re-read with it, because a plan change moves
	 * the monthly allowance the low-credit warning is measured against.
	 */
	private refreshSubscriptions(): void {
		// TRAP: not tied to this page's lifetime on purpose — a store read
		// cancelled by navigating away straight after the change would leave the
		// app on the old plan, which is the bug this call exists to close.
		this.subscriptionsState.load().subscribe({ error: () => undefined });
		this.creditBalanceState.refresh();
		this.subscriptionsService
			.getAll()
			.pipe(takeUntil(this.destroy$))
			.subscribe(list => {
				this.subsByProduct = {};
				for (const item of list) {
					this.subsByProduct[item.subscription.product_id] = item;
				}
				this.recomputeActiveRows(
					this.productTabControl.value?.productId
				);
			});
	}

	private loadAll(): void {
		this.loading = true;
		forkJoin([
			this.catalogService.getProducts(),
			this.catalogService.getAllPlans(),
			this.subscriptionsService.getAll()
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ([products, plans, subs]) => {
					this.plansByProduct = plans as Record<
						number,
						CatalogPlan[]
					>;
					this.subsByProduct = {};
					for (const item of subs) {
						this.subsByProduct[item.subscription.product_id] = item;
					}
					this.applyProductTabs(products);
					this.loading = false;
				},
				error: () => {
					this.plansByProduct = {};
					this.subsByProduct = {};
					this.productTabs = [];
					this.activePlanRows = [];
					this.loading = false;
				}
			});
	}

	/**
	 * The tabs, and which one opens — both from {@link buildProductTabs} /
	 * {@link defaultProductTab}, shared with the public pricing page so the two
	 * screens never disagree about the order of the products or about which one a
	 * visitor lands on.
	 *
	 * The order used to be the catalogue's and the default used to be the first
	 * tab, which on dev opened this page on the Creator Data API rather than on
	 * Lolly Platform. The allow-list scoping that predates that (a tab that sells
	 * nothing is not a tab) and the fallback to every active product both live in
	 * the shared helper now; see it for the reasoning.
	 */
	private applyProductTabs(products: Product[]): void {
		this.productTabs = buildProductTabs(products);
		const initial = defaultProductTab(
			this.productTabs,
			this.requestedProductId()
		);
		this.productTabItems = this.productTabs.map(tab => ({
			id: String(tab.productId),
			label: tab.title
		}));
		this.activeTabId = initial ? String(initial.productId) : '';
		this.productTabControl.setValue(initial, { emitEvent: false });
		this.recomputeActiveRows(initial?.productId);
	}

	/** A product tab was chosen on the strip: the control stays the owner of the
	 *  choice, so everything that reads it keeps working. */
	onProductTab(id: string): void {
		const tab = this.productTabs.find(
			option => String(option.productId) === id
		);
		if (!tab || tab.productId === this.productTabControl.value?.productId) {
			return;
		}
		this.activeTabId = id;
		this.productTabControl.setValue(tab);
	}

	private requestedProductId(): number | null {
		const param = this.route.snapshot.queryParamMap.get('product_id');
		if (!param) return null;
		const id = Number(param);
		return Number.isFinite(id) ? id : null;
	}

	cancelScheduledChange(): void {
		const banner = this.scheduledBanner;
		if (!banner || this.scheduledActionInFlight) return;
		const isDowngrade = banner.changeType === ScheduledChangeType.DOWNGRADE;
		const label = isDowngrade ? 'downgrade' : 'cancellation';
		// Downgrade schedules are released via the dedicated endpoint;
		// /uncancel only clears a pending cancel and is a no-op for a
		// downgrade schedule (leaves the Stripe schedule + DB row active).
		const request$ = isDowngrade
			? this.releaseService.release(banner.productId)
			: this.uncancelService.uncancel(banner.productId);
		this.scheduledActionInFlight = true;
		request$.pipe(takeUntil(this.destroy$)).subscribe({
			next: () => {
				this.scheduledActionInFlight = false;
				this.toasts.success(`Scheduled ${label} reverted.`);
				this.refreshSubscriptions();
			},
			error: err => {
				this.scheduledActionInFlight = false;
				this.billingErrors.handle(err, `Could not undo ${label}.`);
			}
		});
	}

	onToggleAnnually(checked: boolean): void {
		this.annually = checked;
		this.recomputeActiveRows(this.productTabControl.value?.productId);
	}

	private recomputeActiveRows(productId: number | undefined): void {
		this.activeProductId = productId ?? undefined;
		if (productId === undefined || productId === null) {
			this.activePlanRows = [];
			this.scheduledBanner = null;
			this.activeEnterprisePlan = null;
			this.currentPlanTierLevel = null;
			return;
		}
		this.activeEnterprisePlan = this.enterpriseByProduct[productId] ?? null;
		const currentSub = this.subsByProduct[productId];
		this.currentPlanTierLevel = currentSub?.plan?.tier_level ?? null;
		const onPaid =
			!!currentSub && currentSub.phase !== SubscriptionPhase.APP_TRIAL;
		const interval = this.annually ? PlanInterval.YEAR : PlanInterval.MONTH;
		const allProductPlans = this.plansByProduct[productId] ?? [];
		const plans = allProductPlans.filter(plan => {
			// App-trial tier is always shown (it has no yearly variant), but
			// hidden once on a paid plan — it can't be re-entered.
			if (plan.trial_kind === TrialKind.APP) {
				return !onPaid;
			}
			// Paid tiers: show only the ones matching the selected interval.
			return plan.interval === interval;
		});
		this.showAnnualToggle = hasYearlyVariant(allProductPlans);
		this.annualSavings = computeAnnualSavings(allProductPlans) ?? 0;
		this.annualFreeMonths = computeAnnualFreeMonths(allProductPlans);
		// The Free tier is not offered here: a brand on it sees that on Billing,
		// and a paying brand must not be able to step back down to free access.
		this.activePlanRows = this.isPlatformProduct(allProductPlans)
			? this.toTierRows(
					allProductPlans,
					interval,
					currentSub,
					allProductPlans
			  ).filter(row => parseFloat(row.price) !== 0)
			: plans.map(plan => this.toRow(plan, currentSub, allProductPlans));

		// Creator Data API renders slider cards (Report + Quick), one card per
		// family — detected by the plans' alias prefix.
		const apiPrefix = `${ProductAlias.CREATOR_DATA_API}_`;
		this.isCreatorDataApi = allProductPlans.some(plan =>
			plan.alias.startsWith(apiPrefix)
		);
		if (this.isCreatorDataApi) {
			this.apiRowsById = {};
			for (const row of this.activePlanRows) {
				this.apiRowsById[row.id] = row;
			}
			this.reportTiers = this.buildApiTiers(
				this.activePlanRows,
				'report',
				allProductPlans
			);
			this.quickTiers = this.buildApiTiers(
				this.activePlanRows,
				'quick',
				allProductPlans
			);
		} else {
			this.reportTiers = [];
			this.quickTiers = [];
		}

		this.scheduledBanner = this.buildScheduledBanner(productId, currentSub);
	}

	/** Creator Data API slider card emitted the chosen plan id → dispatch its action. */
	onSelectApiPlanId(planId: number): void {
		const row = this.apiRowsById[planId];
		if (row) {
			this.onPlanAction(row);
		}
	}

	/**
	 * Build one family's ("report"/"quick") slider stops from the already-computed
	 * plan rows (action + price precomputed). Report is priced per credit, Quick
	 * per request (1 request = 0.05 credit ⇒ requests = credits × 20).
	 */
	private buildApiTiers(
		rows: PlanRow[],
		family: 'report' | 'quick',
		allPlans: CatalogPlan[]
	): ApiTierView[] {
		const prefix = `${ProductAlias.CREATOR_DATA_API}_${family}_`;
		return rows
			.filter(row => row.alias.startsWith(prefix))
			.sort((a, b) => a.tier_level - b.tier_level)
			.map(row => {
				const credits = this.creditsOf(row);
				const monthly = parseFloat(row.monthlyPrice);
				const isReport = family === 'report';
				const units = isReport ? credits : credits * 20;
				const perUnit = units > 0 ? monthly / units : 0;
				// Always a per-month price (yearly tiers divided by 12), striking
				// the regular monthly price so toggling to Annual shows the saving
				// — the same principle as the ordinary plan cards.
				return {
					planId: row.id,
					tierLevel: row.tier_level,
					priceValue: this.formatApiPrice(row.monthlyPrice),
					priceStruck: this.apiStruckMonthly(row, allPlans),
					priceMeta: '/ month',
					volumeLabel: isReport
						? `${units.toLocaleString('en-US')} credits/mo`
						: `${units.toLocaleString('en-US')} requests/mo`,
					perUnitLabel: isReport
						? `$${perUnit.toFixed(3)} / credit`
						: `$${perUnit.toFixed(4)} / request`,
					actionLabel: row.actionLabel,
					actionType: row.action,
					actionDisabled:
						row.action === 'current' ||
						row.action === 'scheduled' ||
						row.action === 'unavailable'
				};
			});
	}

	/** Struck regular monthly price for a discounted yearly tier — the matching
	 *  monthly plan's own price. Null for a monthly tier, or when the year isn't
	 *  actually cheaper per month. */
	private apiStruckMonthly(
		plan: CatalogPlan,
		allPlans: CatalogPlan[]
	): string | null {
		if (plan.interval !== PlanInterval.YEAR) {
			return null;
		}
		const twinMonthly = monthlyTwinPrice(plan, allPlans);
		if (twinMonthly === null) {
			return null;
		}
		const monthly = parseFloat(toMonthlyPrice(plan));
		// Only strike when there's a real saving (avoid "945 struck / 945").
		if (isNaN(monthly) || twinMonthly <= monthly + 0.005) {
			return null;
		}
		return this.formatApiPrice(String(twinMonthly));
	}

	/** Raw plan price with thousands separators, e.g. "11,340.00" / "945.00". */
	private formatApiPrice(price: string): string {
		const value = parseFloat(price);
		if (isNaN(value)) {
			return price;
		}
		return value.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		});
	}

	/** Monthly credit allowance from the plan's api_credits_grant_per_month cap. */
	private creditsOf(plan: CatalogPlan): number {
		const entitlement = (plan.entitlements ?? []).find(
			row =>
				row.alias === PlanEntitlementAlias.API_CREDITS_GRANT_PER_MONTH
		);
		return entitlement?.cap ?? 0;
	}

	private buildScheduledBanner(
		productId: number,
		currentSub: ProductSubscription | undefined
	): typeof this.scheduledBanner {
		const change = currentSub?.scheduled_change;
		if (!change || change.cancelled_at) return null;
		if (
			change.change_type !== ScheduledChangeType.DOWNGRADE &&
			change.change_type !== ScheduledChangeType.CANCEL
		) {
			return null;
		}
		let targetPlanTitle: string | null = null;
		if (
			change.target_plan_id !== null &&
			change.target_plan_id !== undefined
		) {
			const plan = (this.plansByProduct[productId] ?? []).find(
				p => p.id === change.target_plan_id
			);
			targetPlanTitle = plan?.title ?? null;
		}
		const date = this.formatDate(change.scheduled_for);
		return {
			changeType: change.change_type,
			targetPlanTitle,
			date,
			message:
				change.change_type === ScheduledChangeType.DOWNGRADE
					? `Downgrade${
							targetPlanTitle ? ` to ${targetPlanTitle}` : ''
					  } scheduled on ${date}`
					: `Cancellation scheduled on ${date}`,
			productId
		};
	}

	/** True when these plans are the one application product's (see
	 *  {@link PLATFORM_PLAN_PREFIX}). Matched on the alias, not on an id. */
	private isPlatformProduct(plans: CatalogPlan[]): boolean {
		return plans.some(plan => plan.alias.startsWith(PLATFORM_PLAN_PREFIX));
	}

	/** A plan's monthly credit allowance, in whole credits. `null` when the plan
	 *  states none — the row is absent on a product that has no ladder. */
	private allowanceOf(plan: CatalogPlan): number | null {
		return (
			(plan.entitlements ?? []).find(
				row =>
					row.alias ===
					PlanEntitlementAlias.APP_CREDITS_GRANT_PER_MONTH
			)?.cap ?? null
		);
	}

	/**
	 * One row per TIER, with the tier's rungs behind a slider.
	 *
	 * Ordering is by allowance, not by price or by id: the ladder is what the
	 * slider moves along, and two rungs can only differ by their allowance and
	 * the price that follows from it. A rung with no allowance stated sorts
	 * last rather than being dropped — a plan the catalogue cannot describe
	 * should still be buyable.
	 */
	private toTierRows(
		plans: CatalogPlan[],
		interval: PlanInterval,
		currentSub: ProductSubscription | undefined,
		allProductPlans: CatalogPlan[]
	): PlanRow[] {
		const byTier = new Map<number, CatalogPlan[]>();
		for (const plan of plans) {
			const rungs = byTier.get(plan.tier_level) ?? [];
			rungs.push(plan);
			byTier.set(plan.tier_level, rungs);
		}

		const rows: PlanRow[] = [];
		for (const tier of Array.from(byTier.keys()).sort((a, b) => a - b)) {
			// THE INTERVAL IS FILTERED PER TIER, NOT OVER THE PRODUCT, and a
			// tier that has nothing at the chosen interval falls back to what it
			// does have. That is the free tier: it is priced at $0 and carries no
			// yearly twin (a $0 yearly row would be a second row saying nothing),
			// so a product-wide filter made the Free card DISAPPEAR the moment
			// the brand looked at annual pricing — the cheapest way in, hidden by
			// a toggle about how often you pay.
			const atInterval = (byTier.get(tier) ?? []).filter(
				plan => plan.interval === interval
			);
			const rungs = (
				atInterval.length > 0 ? atInterval : byTier.get(tier) ?? []
			).sort(
				(a, b) =>
					(this.allowanceOf(a) ?? Number.MAX_SAFE_INTEGER) -
					(this.allowanceOf(b) ?? Number.MAX_SAFE_INTEGER)
			);
			const index = this.rungIndexFor(tier, rungs, currentSub);
			const plan = rungs[index] ?? rungs[0];
			if (!plan) {
				continue;
			}
			const ladder = buildPlanLadder(rungs, rung =>
				this.allowanceOf(rung)
			);
			rows.push({
				...this.toRow(plan, currentSub, allProductPlans),
				ladder: ladder.stops,
				ladderIndex: index,
				ladderFooter: ladder.footerNote
			});
		}
		return rows;
	}

	/**
	 * Which rung a tier opens on: the brand's own, when its current plan is in
	 * this tier, and otherwise the cheapest. Once the brand moves the slider,
	 * its choice is what persists — {@link platformRungByTier} is keyed by tier
	 * and survives an interval toggle, which is the one case where the rungs are
	 * rebuilt under a selection the brand has already made.
	 */
	private rungIndexFor(
		tier: number,
		rungs: CatalogPlan[],
		currentSub: ProductSubscription | undefined
	): number {
		const chosen = this.platformRungByTier[tier];
		if (chosen !== undefined) {
			return Math.min(chosen, Math.max(0, rungs.length - 1));
		}
		const current = rungs.findIndex(
			rung => rung.id === currentSub?.plan?.id
		);
		return current >= 0 ? current : 0;
	}

	/**
	 * The full list for the rung the card is showing. A brand that already holds
	 * a plan gets the same rows WITH its usage against them on the billing page;
	 * this is the comparable list for the tiers it does not hold.
	 */
	onPlanDetails(plan: PlanRow): void {
		this.planDetailsDialog.open(
			plan,
			plan.ladder[plan.ladderIndex]?.label ?? '',
			plan.cardTitle
		);
	}

	/** A platform card's slider moved — remember the rung and redraw the tier. */
	onLadderIndexChange(plan: PlanRow, index: number): void {
		if (this.platformRungByTier[plan.tier_level] === index) {
			return;
		}
		this.platformRungByTier[plan.tier_level] = index;
		this.recomputeActiveRows(this.productTabControl.value?.productId);
	}

	private toRow(
		plan: CatalogPlan,
		currentSub: ProductSubscription | undefined,
		allProductPlans: CatalogPlan[]
	): PlanRow {
		const action = this.computeAction(plan, currentSub);
		const allowanceChange =
			(action === 'upgrade' || action === 'downgrade') &&
			this.isAllowanceChange(plan, currentSub);
		const allowanceCredits = this.allowanceOf(plan);
		return {
			...plan,
			action,
			actionLabel: allowanceChange
				? `Change to ${this.creditsPhrase(allowanceCredits)}`
				: this.labelFor(action, plan),
			actionInFlight: false,
			allowanceChange,
			allowanceCredits,
			actionNote: allowanceChange
				? this.allowanceChangeNote(action, currentSub)
				: '',
			monthlyPrice: toMonthlyPrice(plan),
			struckMonthlyPrice: struckMonthlyPrice(plan, allProductPlans),
			// A free trial only applies to a fresh subscribe — never to an
			// upgrade/downgrade/current plan (those convert in place server-side).
			hasTrial: planHasTrial(plan) && action === 'subscribe',
			featureRows: buildFeatureRows(plan),
			cardTitle: planCardTitle(plan),
			ladder: [],
			ladderIndex: 0,
			ladderFooter: '',
			subtitle: applicationTierCopy(plan)?.subtitle ?? '',
			priceNote:
				annualBillingNote(plan) ??
				applicationTierCopy(plan)?.priceNote ??
				''
		};
	}

	private computeAction(
		plan: CatalogPlan,
		currentSub: ProductSubscription | undefined
	): PlanAction {
		if (!plan.is_active) return 'unavailable';
		if (currentSub && currentSub.plan.id === plan.id) return 'current';
		if (!currentSub) {
			// Fresh purchase on this product. App-trial-capable plans (APP or
			// BOTH) are purchasable per-user: an eligible user gets the no-card
			// app-trial, otherwise the plan is bought via Stripe — both go
			// through the single 'subscribe' action, which branches on the
			// backend checkout response. Only dead-end when neither is possible
			// (a pure-APP plan the user already trialed, with no Stripe price).
			const appTrialCapable =
				plan.trial_kind === TrialKind.APP ||
				plan.trial_kind === TrialKind.BOTH;
			if (
				appTrialCapable &&
				!plan.app_trial_eligible &&
				!plan.stripe_purchasable
			) {
				return 'unavailable';
			}
			return 'subscribe';
		}
		// On an app-trial: any paid tier is a fresh purchase via hosted
		// checkout (backend converts the trial), not an in-place
		// upgrade/downgrade. Surface every paid plan as "Subscribe".
		if (currentSub.phase === SubscriptionPhase.APP_TRIAL)
			return 'subscribe';

		const pending = currentSub.scheduled_change;
		const hasPending = !!pending && !pending.cancelled_at;
		if (hasPending && pending!.target_plan_id === plan.id) {
			return 'scheduled';
		}

		// Direction within a product is decided by tier first, then by
		// billing interval when the tier is unchanged:
		//   - higher tier                 → upgrade (immediate, proration)
		//   - lower tier                  → downgrade (scheduled, period end)
		//   - same tier, month → year     → upgrade
		//   - same tier, year  → month    → downgrade
		//   - same tier and interval      → by monthly allowance, the third
		//                                   rank key the backend's _plan_rank
		//                                   uses: more credits → upgrade,
		//                                   fewer → downgrade
		// Downgrades are blocked while another change is already pending —
		// the backend rejects a second schedule; the user must undo first.
		const direction =
			this.changeDirection(plan, currentSub.plan) ??
			this.allowanceDirection(plan, currentSub);
		if (direction === 'upgrade') return 'upgrade';
		if (direction === 'downgrade') {
			return hasPending ? 'unavailable' : 'downgrade';
		}
		// A platform rung the brand cannot move to in place. "Subscribe" here
		// was a dead end: the purchase answers 409 already_subscribed.
		if (this.isPlatformPlan(plan)) return 'unavailable';
		return 'subscribe';
	}

	/**
	 * Same tier, same interval, different allowance — the move the Plans page
	 * offers as "Change to N credits". Null when the tier or the interval
	 * differs (that is {@link changeDirection}'s question), when either side
	 * states no allowance, or when the two allowances are equal.
	 */
	private allowanceDirection(
		plan: CatalogPlan,
		currentSub: ProductSubscription
	): 'upgrade' | 'downgrade' | null {
		if (
			!this.isPlatformPlan(plan) ||
			plan.tier_level !== currentSub.plan.tier_level ||
			plan.interval !== currentSub.plan.interval
		) {
			return null;
		}
		const target = this.allowanceOf(plan);
		const current = this.currentAllowanceOf(currentSub);
		if (target === null || current === null || target === current) {
			return null;
		}
		return target > current ? 'upgrade' : 'downgrade';
	}

	private isAllowanceChange(
		plan: CatalogPlan,
		currentSub: ProductSubscription | undefined
	): boolean {
		return (
			!!currentSub && this.allowanceDirection(plan, currentSub) !== null
		);
	}

	/**
	 * The allowance the brand holds now: its plan's catalogue row first (the
	 * same read the ladder is sorted by), the subscription's own entitlement
	 * when the catalogue no longer lists that plan.
	 */
	private currentAllowanceOf(currentSub: ProductSubscription): number | null {
		const catalogPlan = (
			this.plansByProduct[Number(currentSub.plan.product_id)] ?? []
		).find(plan => plan.id === currentSub.plan.id);
		if (catalogPlan) {
			return this.allowanceOf(catalogPlan);
		}
		return (
			currentSub.entitlements.find(
				entitlement =>
					entitlement.alias ===
					PlanEntitlementAlias.APP_CREDITS_GRANT_PER_MONTH
			)?.cap ?? null
		);
	}

	/**
	 * The line under an allowance change, before it is taken.
	 *
	 * CONTRACT: the backend applies a larger allowance at once with Stripe
	 * proration and grants the difference for the current month; a smaller one
	 * is scheduled for the end of the period (design D4 of
	 * billing-keeps-its-promises).
	 */
	private allowanceChangeNote(
		action: PlanAction,
		currentSub: ProductSubscription | undefined
	): string {
		if (action === 'upgrade') {
			return 'Charged now, prorated — the extra credits land today.';
		}
		if (action === 'downgrade' && currentSub) {
			return `From ${this.formatDate(
				currentSub.period_end
			)}, when this billing period ends.`;
		}
		return '';
	}

	private isPlatformPlan(plan: CatalogPlan): boolean {
		return plan.alias.startsWith(PLATFORM_PLAN_PREFIX);
	}

	/** "800 credits", grouped; the plan's own title when it states none. */
	private creditsPhrase(credits: number | null): string {
		return credits === null
			? 'this allowance'
			: `${credits.toLocaleString('en-US')} credits`;
	}

	/**
	 * Whether moving from the current plan to `target` (same product) is an
	 * upgrade or a downgrade. Tier dominates; ties break on interval
	 * (yearly ranks above monthly). Returns null when neither applies.
	 */
	private changeDirection(
		target: { tier_level: number; interval: PlanInterval },
		current: { tier_level: number; interval: PlanInterval }
	): 'upgrade' | 'downgrade' | null {
		if (target.tier_level > current.tier_level) return 'upgrade';
		if (target.tier_level < current.tier_level) return 'downgrade';
		const targetRank = this.intervalRank(target.interval);
		const currentRank = this.intervalRank(current.interval);
		if (targetRank > currentRank) return 'upgrade';
		if (targetRank < currentRank) return 'downgrade';
		return null;
	}

	/** Yearly outranks monthly so month→year is an upgrade, year→month a downgrade. */
	private intervalRank(interval: PlanInterval): number {
		return interval === PlanInterval.YEAR ? 1 : 0;
	}

	private labelFor(action: PlanAction, plan?: CatalogPlan): string {
		switch (action) {
			case 'current':
				return 'Current Plan';
			case 'subscribe':
				// An eligible user gets the no-card app-trial from this button;
				// everyone else buys via Stripe.
				return plan?.app_trial_eligible
					? 'Start free trial'
					: 'Subscribe';
			case 'upgrade':
				return 'Upgrade';
			case 'downgrade':
				return 'Downgrade';
			case 'scheduled':
				return 'Scheduled';
			case 'unavailable':
				return 'Unavailable';
		}
	}

	private formatDate(iso: string): string {
		try {
			return new Date(iso).toLocaleDateString(undefined, {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			});
		} catch {
			return iso;
		}
	}
}
